/**
 * SettingsUtils Module
 *
 * Utility functions for managing office employee payslip settings.
 * Handles loading, saving, and converting between API and legacy formats.
 * Supports both salary (給与) and bonus (賞与) payslip types.
 *
 * @module SettingsUtils
 */

import {
  paymentItems,
  deductionItems,
  attendanceItems,
  bonusPaymentItems,
  bonusDeductionItems,
  bonusAttendanceItems,
  salaryNotesItems,
  bonusNotesItems
} from './DataConfiguration';
import { PayslipSettingsService, PayslipSettings as ApiPayslipSettings, FieldSettings } from '../../../../services/PayslipSettingsService';

/**
 * Default enabled payment items for salary payslips.
 * Includes first 25 items plus important summary fields (課税支給額, 非課支給額, etc.).
 *
 * @constant
 */
const defaultEnabledPaymentItems = [
  ...paymentItems.slice(0, 25).map(item => item.id), // First 25 items
  'taxable_payment',     // 課税支給額
  'non_taxable_payment', // 非課支給額
  'taxable_payment_cumulative', // 課税支給累計
  'total_payment'        // 支給合計
];

/**
 * Default enabled deduction items (first 9 items).
 *
 * @constant
 */
const defaultEnabledDeductionItems = deductionItems.slice(0, 9).map(item => item.id);

/**
 * Default enabled attendance items (first 9 items).
 *
 * @constant
 */
const defaultEnabledAttendanceItems = attendanceItems.slice(0, 9).map(item => item.id);

/**
 * PayslipSettings interface.
 *
 * Configuration interface for office employee payslip appearance and field visibility.
 *
 * @interface
 */
export interface PayslipSettings {
  borderColor: string;              // Border color for payslip table (hex color)
  backgroundColor: string;          // Background color for payslip table (hex color)
  fontSize: string;                 // Font size (e.g., "10px")
  fontFamily: string;               // Font family (e.g., "Yu Gothic")
  showCompanyName: boolean;         // Whether to show company name
  showEmployeeCode: boolean;        // Whether to show employee code
  showMemo: boolean;                // Whether to show memo section
  payment: {                        // Payment section field settings
    enabled: string[];              // Array of enabled payment field IDs
    lastUpdated: string;            // ISO timestamp of last update
    updatedBy: string;              // User who last updated (e.g., "admin")
  };
  deduction: {                      // Deduction section field settings
    enabled: string[];              // Array of enabled deduction field IDs
    lastUpdated: string;            // ISO timestamp of last update
    updatedBy: string;              // User who last updated
  };
  attendance: {                     // Attendance section field settings
    enabled: string[];              // Array of enabled attendance field IDs
    lastUpdated: string;            // ISO timestamp of last update
    updatedBy: string;              // User who last updated
  };
  notes: {                          // Notes section field settings
    enabled: string[];              // Array of enabled notes field IDs
    lastUpdated: string;            // ISO timestamp of last update
    updatedBy: string;              // User who last updated
  };
}

/**
 * Loads payslip settings from API with localStorage fallback.
 *
 * Attempts to fetch settings from PayslipSettingsService API first.
 * If API fails, falls back to localStorage or returns default settings.
 *
 * @param dataType - Type of payslip settings to load ('salary' or 'bonus')
 * @returns Promise resolving to PayslipSettings
 *
 * @example
 * ```typescript
 * const settings = await loadSettings('salary');
 * ```
 */
export const loadSettings = async (dataType: 'salary' | 'bonus'): Promise<PayslipSettings> => {
  try {
    // Try to get settings from API first
    const apiSettings = await PayslipSettingsService.getSettingsWithFallback(dataType);
    
    if (apiSettings) {
      // Ensure all required fields are present and convert to legacy format
      return convertApiSettingsToLegacy(apiSettings, dataType);
    }
  } catch (error) {
    console.error('Error loading settings from API:', error);
  }
  
  // Return default settings if no settings found
  return getDefaultSettings(dataType);
};

/**
 * Converts API settings format to legacy PayslipSettings format.
 *
 * Maps API format to internal PayslipSettings interface.
 * Ensures all required fields are present with default values.
 *
 * @param apiSettings - Settings from API (ApiPayslipSettings format)
 * @param dataType - Type of payslip ('salary' or 'bonus')
 * @returns PayslipSettings in legacy format
 *
 * @private
 */
const convertApiSettingsToLegacy = (apiSettings: ApiPayslipSettings, dataType: 'salary' | 'bonus'): PayslipSettings => {
  const legacySettings: PayslipSettings = {
    borderColor: apiSettings.borderColor || '#000000',
    backgroundColor: apiSettings.backgroundColor || '#ffffff',
    fontSize: apiSettings.fontSize || '10px',
    fontFamily: apiSettings.fontFamily || 'Yu Gothic',
    showCompanyName: apiSettings.showCompanyName !== undefined ? apiSettings.showCompanyName : true,
    showEmployeeCode: apiSettings.showEmployeeCode !== undefined ? apiSettings.showEmployeeCode : true,
    showMemo: apiSettings.showMemo !== undefined ? apiSettings.showMemo : true,
    payment: ensureFieldSettings(apiSettings.payment, dataType === 'salary' ? defaultEnabledPaymentItems : bonusPaymentItems.map(item => item.id)),
    deduction: ensureFieldSettings(apiSettings.deduction, dataType === 'salary' ? defaultEnabledDeductionItems : bonusDeductionItems.map(item => item.id)),
    attendance: ensureFieldSettings(apiSettings.attendance, dataType === 'salary' ? defaultEnabledAttendanceItems : bonusAttendanceItems.slice(0, 1).map(item => item.id)),
    notes: ensureFieldSettings(apiSettings.notes, dataType === 'salary' ? salaryNotesItems.slice(0, 6).map(item => item.id) : bonusNotesItems.map(item => item.id))
  };
  
  return legacySettings;
};

/**
 * Ensures field settings are complete with defensive checks for older browser compatibility.
 *
 * Validates and provides default values for field settings.
 * Added defensive checks to prevent errors on older browsers that may not support all array methods.
 *
 * @param fieldSettings - Field settings from API (may be undefined)
 * @param defaultEnabled - Default enabled field IDs if settings are missing
 * @returns Complete FieldSettings object with defaults applied
 *
 * @private
 */
const ensureFieldSettings = (fieldSettings: FieldSettings | undefined, defaultEnabled: string[]): FieldSettings => {
  try {
    if (!fieldSettings || !fieldSettings.enabled) {
      return {
        enabled: Array.isArray(defaultEnabled) ? defaultEnabled : [],
        lastUpdated: new Date().toISOString(),
        updatedBy: 'admin'
      };
    }
    
    // Ensure enabled is always an array - critical for older browsers
    const enabledArray = Array.isArray(fieldSettings.enabled) 
      ? fieldSettings.enabled 
      : (Array.isArray(defaultEnabled) ? defaultEnabled : []);
    
    return {
      enabled: enabledArray,
      lastUpdated: fieldSettings.lastUpdated || new Date().toISOString(),
      updatedBy: fieldSettings.updatedBy || 'admin'
    };
  } catch (error) {
    console.warn('Error ensuring field settings:', error);
    return {
      enabled: Array.isArray(defaultEnabled) ? defaultEnabled : [],
      lastUpdated: new Date().toISOString(),
      updatedBy: 'admin'
    };
  }
};

/**
 * Gets default payslip settings for a given data type.
 *
 * Returns default configuration with sensible defaults:
 * - Black borders, white background
 * - 10px font size, Yu Gothic font family
 * - All display options enabled
 * - Default enabled fields based on data type
 *
 * @param dataType - Type of payslip ('salary' or 'bonus')
 * @returns Default PayslipSettings
 *
 * @example
 * ```typescript
 * const defaultSalarySettings = getDefaultSettings('salary');
 * const defaultBonusSettings = getDefaultSettings('bonus');
 * ```
 */
export const getDefaultSettings = (dataType: 'salary' | 'bonus'): PayslipSettings => {
  return {
    borderColor: '#000000',
    backgroundColor: '#ffffff',
    fontSize: '10px',
    fontFamily: 'Yu Gothic',
    showCompanyName: true,
    showEmployeeCode: true,
    showMemo: true,
    payment: {
      enabled: dataType === 'salary' ? defaultEnabledPaymentItems : bonusPaymentItems.map(item => item.id),
      lastUpdated: new Date().toISOString(),
      updatedBy: 'admin'
    },
    deduction: {
      enabled: dataType === 'salary' ? defaultEnabledDeductionItems : bonusDeductionItems.map(item => item.id),
      lastUpdated: new Date().toISOString(),
      updatedBy: 'admin'
    },
    attendance: {
      enabled: dataType === 'salary' ? defaultEnabledAttendanceItems : bonusAttendanceItems.slice(0, 1).map(item => item.id),
      lastUpdated: new Date().toISOString(),
      updatedBy: 'admin'
    },
    notes: {
      enabled: dataType === 'salary' ? salaryNotesItems.slice(0, 6).map(item => item.id) : bonusNotesItems.map(item => item.id),
      lastUpdated: new Date().toISOString(),
      updatedBy: 'admin'
    }
  };
};

/**
 * Saves payslip settings to API with localStorage fallback.
 *
 * Converts legacy settings format to API format and saves via PayslipSettingsService.
 * Falls back to localStorage if API save fails.
 *
 * @param settings - PayslipSettings to save
 * @param dataType - Type of payslip ('salary' or 'bonus')
 * @returns Promise resolving to true if save succeeded, false otherwise
 *
 * @example
 * ```typescript
 * const success = await saveSettings(mySettings, 'salary');
 * if (success) {
 *   console.log('Settings saved successfully');
 * }
 * ```
 */
export const saveSettings = async (settings: PayslipSettings, dataType: 'salary' | 'bonus'): Promise<boolean> => {
  try {
    // Debug: Log the settings being saved (for development)
    console.log(`[DEBUG] Saving settings for ${dataType}:`, settings);
    
    // Convert legacy settings format to API format
    const apiSettings = convertLegacySettingsToApi(settings, dataType);
    
    // Debug: Log the converted API settings (for development)
    console.log(`[DEBUG] Converted API settings for ${dataType}:`, apiSettings);
    
    // Try to save to API first
    const success = await PayslipSettingsService.saveSettingsWithFallback(dataType, apiSettings);
    
    if (success) {
      console.log(`Settings saved successfully for ${dataType}`);
      return true;
    } else {
      console.error(`Failed to save settings for ${dataType}`);
      return false;
    }
  } catch (error) {
    console.error(`Error saving settings for ${dataType}:`, error);
    return false;
  }
};

/**
 * Converts legacy PayslipSettings format to API format.
 *
 * Maps internal PayslipSettings interface to ApiPayslipSettings format
 * expected by PayslipSettingsService.
 *
 * @param legacySettings - Settings in legacy PayslipSettings format
 * @param dataType - Type of payslip ('salary' or 'bonus')
 * @returns ApiPayslipSettings in API format
 *
 * @private
 */
const convertLegacySettingsToApi = (legacySettings: PayslipSettings, dataType: 'salary' | 'bonus'): ApiPayslipSettings => {
  return {
    dataType,
    borderColor: legacySettings.borderColor,
    backgroundColor: legacySettings.backgroundColor,
    fontSize: legacySettings.fontSize,
    fontFamily: legacySettings.fontFamily,
    showCompanyName: legacySettings.showCompanyName,
    showEmployeeCode: legacySettings.showEmployeeCode,
    showMemo: legacySettings.showMemo,
    payment: legacySettings.payment,
    deduction: legacySettings.deduction,
    attendance: legacySettings.attendance,
    notes: legacySettings.notes
  };
};

/**
 * Updates specific field settings for a payslip type.
 *
 * Updates only the field settings for a specific section (payment, deduction, attendance, or notes)
 * without modifying other settings. Useful for partial updates.
 *
 * @param dataType - Type of payslip ('salary' or 'bonus')
 * @param fieldType - Type of field section to update ('payment', 'deduction', 'attendance', or 'notes')
 * @param fieldSettings - New field settings to apply
 * @returns Promise resolving to true if update succeeded, false otherwise
 *
 * @example
 * ```typescript
 * const newPaymentSettings = {
 *   enabled: ['basic_salary', 'position_allowance'],
 *   lastUpdated: new Date().toISOString(),
 *   updatedBy: 'admin'
 * };
 * await updateFieldSettings('salary', 'payment', newPaymentSettings);
 * ```
 */
export const updateFieldSettings = async (
  dataType: 'salary' | 'bonus',
  fieldType: 'payment' | 'deduction' | 'attendance' | 'notes',
  fieldSettings: FieldSettings
): Promise<boolean> => {
  try {
    const success = await PayslipSettingsService.updateFieldSettings(dataType, fieldType, fieldSettings);
    
    if (success) {
      console.log(`${fieldType} settings updated successfully for ${dataType}`);
      return true;
    } else {
      console.error(`Failed to update ${fieldType} settings for ${dataType}`);
      return false;
    }
  } catch (error) {
    console.error(`Error updating ${fieldType} settings for ${dataType}:`, error);
    return false;
  }
};

/**
 * Loads payslip settings from localStorage (legacy function).
 *
 * @deprecated This function is kept for backward compatibility during migration.
 *             New code should use loadSettings() which uses API with localStorage fallback.
 *
 * Loads settings directly from localStorage without API fallback.
 * Ensures all required fields are present, especially for salary settings.
 *
 * @param dataType - Type of payslip settings to load ('salary' or 'bonus')
 * @returns PayslipSettings from localStorage or default settings if not found
 *
 * @private
 */
export const loadSettingsFromLocalStorage = (dataType: 'salary' | 'bonus'): PayslipSettings => {
  try {
    const storageKey = `payslipSettings_${dataType}`;
    const savedSettings = localStorage.getItem(storageKey);
    if (savedSettings) {
      const parsed = JSON.parse(savedSettings);
      
      /**
       * Ensure payment settings exist and include required fields.
       * For salary settings, ensures important fields like taxable payment totals are included.
       */
      if (!parsed.payment) {
        parsed.payment = {
          enabled: dataType === 'salary' ? defaultEnabledPaymentItems : bonusPaymentItems.map(item => item.id),
          lastUpdated: new Date().toISOString(),
          updatedBy: 'admin'
        };
      } else if (dataType === 'salary') {
        /**
         * For existing salary settings, ensure important fields are included.
         * These fields are critical for payslip display and must be present.
         */
        const requiredFields = [
          'taxable_commute', 
          'non_taxable_commute',
          'taxable_payment',     // 課税支給額
          'non_taxable_payment', // 非課支給額
          'taxable_payment_cumulative', // 課税支給累計
          'total_payment'        // 支給合計
        ];
        const missingFields = requiredFields.filter(field => !parsed.payment.enabled.includes(field));
        
        if (missingFields.length > 0) {
          parsed.payment.enabled = [...parsed.payment.enabled, ...missingFields];
          parsed.payment.lastUpdated = new Date().toISOString();
          parsed.payment.updatedBy = 'admin';
        }
      }
      
      // Ensure deduction settings exist
      if (!parsed.deduction) {
        parsed.deduction = {
          enabled: dataType === 'salary' ? defaultEnabledDeductionItems : bonusDeductionItems.map(item => item.id),
          lastUpdated: new Date().toISOString(),
          updatedBy: 'admin'
        };
      }
      
      // Ensure attendance settings exist
      if (!parsed.attendance) {
        parsed.attendance = {
          enabled: dataType === 'salary' ? defaultEnabledAttendanceItems : bonusAttendanceItems.slice(0, 1).map(item => item.id),
          lastUpdated: new Date().toISOString(),
          updatedBy: 'admin'
        };
      }
      
      // Ensure notes settings exist
      if (!parsed.notes) {
        parsed.notes = {
          enabled: dataType === 'salary' ? salaryNotesItems.slice(0, 6).map(item => item.id) : bonusNotesItems.map(item => item.id),
          lastUpdated: new Date().toISOString(),
          updatedBy: 'admin'
        };
      }
      
      return parsed;
    }
  } catch (error) {
    console.error('Error loading settings from localStorage:', error);
  }
  
  // Return default settings if no saved settings found
  return getDefaultSettings(dataType);
};

/**
 * Saves payslip settings to localStorage (legacy function).
 *
 * @deprecated This function is kept for backward compatibility during migration.
 *             New code should use saveSettings() which uses API with localStorage fallback.
 *
 * Saves settings directly to localStorage without API call.
 *
 * @param settings - PayslipSettings to save
 * @param dataType - Type of payslip ('salary' or 'bonus')
 *
 * @private
 */
export const saveSettingsToLocalStorage = (settings: PayslipSettings, dataType: 'salary' | 'bonus'): void => {
  try {
    const storageKey = `payslipSettings_${dataType}`;
    localStorage.setItem(storageKey, JSON.stringify(settings));
    console.log(`Settings saved to localStorage for ${dataType}`);
  } catch (error) {
    console.error('Error saving settings to localStorage:', error);
  }
};

/**
 * Clears payslip settings from localStorage.
 *
 * Removes saved settings from localStorage for a given data type.
 * Used for resetting settings to defaults.
 *
 * @param dataType - Type of payslip ('salary' or 'bonus')
 *
 * @example
 * ```typescript
 * clearSettings('salary'); // Clears salary settings from localStorage
 * ```
 */
export const clearSettings = (dataType: 'salary' | 'bonus'): void => {
  try {
    const storageKey = `payslipSettings_${dataType}`;
    localStorage.removeItem(storageKey);
    console.log(`Settings cleared for ${dataType}`);
  } catch (error) {
    console.error('Error clearing settings:', error);
  }
};